import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { listDunnings } from '../../../services/finance/dunningApi';
import { financeErrorMessage } from '../utils/apiError';
import { todayIso } from '../utils/dunningFormat';

import {
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../styles/layout/PageHeader';
import {
   Card,
   EmptyText,
   EmptyTitle,
   EmptyWrap,
   SecondaryButton,
} from '../../../styles/dunning/base';
import { HeaderRow, PageWrap } from '../../../styles/dunning/shell';

import TimelineView from './components/TimelineView';

// ---------------------------------------------------------------------------
// Financeiro › Gestão de cobranças › Timeline cobranças.
//
// Visão GERENCIAL da carga de acompanhamento (adendo 01): quanto ficou para
// trás, quanto cai hoje e quanto vem pela frente. É deliberadamente uma tela
// própria, SEM filtros — nasceu como terceira visão da Central de cobranças e
// foi separada (decisão 2026-08-14) porque a leitura gerencial não deve
// depender do recorte operacional que o cobrador estiver usando lá.
//
// Fetch próprio: sempre TODOS os casos em cobrança (status=OPEN, sem busca,
// sem responsável, sem action_status). Refetch ao focar a janela.
// ---------------------------------------------------------------------------

// Uma página cobre o volume atual (92 casos no backfill). Se passar disso, a
// tela avisa que a leitura é parcial — agregado global é pendência de backend
// (lacuna §8.1 do guia).
const PER_PAGE = 100;

export default function DunningTimeline() {
   const today = useMemo(() => todayIso(), []);

   const [rows, setRows] = useState([]);
   const [meta, setMeta] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [metric, setMetric] = useState('boletos');

   const seqRef = useRef(0);

   const fetchCases = useCallback(async ({ quiet = false } = {}) => {
      seqRef.current += 1;
      const seq = seqRef.current;

      if (!quiet) {
         setLoading(true);
         setError(null);
      }

      try {
         const result = await listDunnings({
            status: 'OPEN',
            page: 1,
            perPage: PER_PAGE,
         });
         if (seqRef.current !== seq) return;
         setRows(result.dunnings);
         setMeta(result.meta);
         setLoading(false);
         setError(null);
      } catch (err) {
         if (seqRef.current !== seq) return;
         setLoading(false);
         if (!quiet) {
            setError(err);
            toast.error(
               financeErrorMessage(err, 'Falha ao carregar os casos.')
            );
         }
      }
   }, []);

   useEffect(() => {
      fetchCases();
   }, [fetchCases]);

   // Sem realtime no backend (webhooks + job 06:00): atualiza ao voltar o
   // foco, em modo silencioso.
   useEffect(() => {
      const onFocus = () => fetchCases({ quiet: true });
      window.addEventListener('focus', onFocus);
      return () => window.removeEventListener('focus', onFocus);
   }, [fetchCases]);

   const hasMorePages = (meta?.totalPages ?? 1) > 1;

   return (
      <PageWrap>
         <HeaderRow>
            <PageHeaderText>
               <PageTitle>Timeline de cobranças</PageTitle>
               <PageSubtitle>
                  Visão geral dos compromissos de acompanhamento de todos os
                  casos em cobrança — independente dos filtros da Central.
               </PageSubtitle>
            </PageHeaderText>
         </HeaderRow>

         {error && !rows.length && !loading ? (
            <Card>
               <EmptyWrap>
                  <EmptyTitle>Algo deu errado</EmptyTitle>
                  <EmptyText>Falha ao carregar os casos.</EmptyText>
                  <div style={{ marginTop: 12 }}>
                     <SecondaryButton type="button" onClick={() => fetchCases()}>
                        Tentar novamente
                     </SecondaryButton>
                  </div>
               </EmptyWrap>
            </Card>
         ) : (
            <>
               <TimelineView
                  rows={rows}
                  today={today}
                  loading={loading}
                  metric={metric}
                  onMetricChange={setMetric}
               />
               {hasMorePages && (
                  <EmptyText style={{ padding: '0 4px' }}>
                     Há mais casos em cobrança do que os {PER_PAGE} mais
                     urgentes carregados — a leitura acima é parcial.
                  </EmptyText>
               )}
            </>
         )}
      </PageWrap>
   );
}
